export type Point = [x: number, y: number];
export type Vector = [u: number, v: number];

export const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

export const inRange = (num: number, min: number, max: number) => {
  return num >= min && num <= max;
};

export const inRange01 = (num: number) => inRange(num, 0, 1);

export const round = (
  value: number,
  precision: number,
  func: 'ceil' | 'floor' | 'round' = 'round',
) => {
  const multiplier = Math.pow(10, precision);
  return Math[func]((value + Number.EPSILON) * multiplier) / multiplier;
};

export const distance = (x: number, y: number) => Math.abs(x - y);

export const point = (x: number, y: number) => [x, y] as Point;

export function pointRotateRads([x, y]: Point, [cx, cy]: Point, angle: number) {
  return point(
    (x - cx) * Math.cos(angle) - (y - cy) * Math.sin(angle) + cx,
    (x - cx) * Math.sin(angle) + (y - cy) * Math.cos(angle) + cy,
  );
}

export const average = (a: number, b: number) => (a + b) / 2;

export function vector(
  x: number,
  y: number,
  originX: number = 0,
  originY: number = 0,
): Vector {
  return [x - originX, y - originY] as Vector;
}

export function vectorAdd(a: Readonly<Vector>, b: Readonly<Vector>): Vector {
  return [a[0] + b[0], a[1] + b[1]] as Vector;
}

export function vectorFromPoint(
  p: Point,
  origin: Point = [0, 0] as Point,
): Vector {
  return vector(p[0] - origin[0], p[1] - origin[1]);
}

export const normalizeRadians = (angle: number) => {
  if (angle < 0) return angle + 2 * Math.PI;
  if (angle >= 2 * Math.PI) return angle - 2 * Math.PI;
  return angle;
};

export const pointCenter = (a: Point, b: Point): Point => {
  return point((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
};

export const snapToAngle = (
  x: number,
  y: number,
  cx: number,
  cy: number,
  snappingAngle: number,
): Point => {
  const SNAPPING_ANGLE = snappingAngle * (Math.PI / 180);

  const dx = x - cx;
  const dy = y - cy;

  const angle = Math.atan2(dy, dx);

  const snappedAngle = Math.round(angle / SNAPPING_ANGLE) * SNAPPING_ANGLE;

  const radius = Math.sqrt(dx * dx + dy * dy);
  const snappedX = cx + radius * Math.cos(snappedAngle);
  const snappedY = cy + radius * Math.sin(snappedAngle);

  return [snappedX, snappedY];
};

export const pointDistance = (a: Point, b: Point): number => {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
};

export const arePointsEqual = (
  point1: Point | undefined,
  point2: Point | undefined,
) => {
  if (point1 === undefined || point2 === undefined) return false;
  return point1[0] === point2[0] && point1[1] === point2[1];
};
